/**
 * Equalize heights of a set of elements.
 *
 * @param {string|Element[]|NodeListOf<Element>} targets
 *   - CSS selector OR list of elements
 * @param {object} [options]
 * @param {number} [options.minWidth=0]       Only equalize if viewport >= minWidth
 * @param {number} [options.debounceMs=100]   Debounce for resize handler
 * @param {boolean} [options.useResizeObserver=true] Watch content changes too
 * @param {boolean} [options.useMinHeight=false] Use min-height instead of height (allows elements to grow if content exceeds)
 * @param {(el: Element) => boolean} [options.filter] Optionally ignore some elements
 *
 * @returns {{ refresh: () => void, destroy: () => void }}
 *
 * Usage:
 * const eq = equalizeHeights(".my-columns", { minWidth: 600 });
 * eq.refresh(); // if you need to manually trigger a recalculation
 * eq.destroy(); // if you need to remove event listeners and inline styles
 */
export function equalizeHeights(targets, options = {}) {
    const {
        minWidth = 0,
        debounceMs = 100,
        useResizeObserver = true,
        useMinHeight = false,
        filter = () => true,
    } = options;

    const els = normalizeTargets(targets).filter(filter);
    if (!els.length) {
        return { refresh() {}, destroy() {} };
    }

    let destroyed = false;
    let ro = null;

    const refresh = () => {
        if (destroyed) return;

        // If we’re under min width, remove inline heights and bail
        if (minWidth && window.innerWidth < minWidth) {
            els.forEach((el) => (el.style.height = ""));
            return;
        }

        // Reset first so measurements reflect natural layout
        els.forEach((el) => (el.style.height = ""));

        // Measure tallest
        let max = 0;
        for (const el of els) {
            // getBoundingClientRect is usually safer than offsetHeight for fractional pixels
            const h = el.getBoundingClientRect().height;
            if (h > max) max = h;
        }

        // Set all to max (avoid setting 0)
        if (max > 0) {
            const px = `${Math.ceil(max)}px`;
            els.forEach((el) => (el.style.height = px));
        }
    };

    const debouncedRefresh = debounce(refresh, debounceMs);

    // window resize
    window.addEventListener("resize", debouncedRefresh, { passive: true });

    // content changes (fonts swap in, images load, dynamic CMS copy, etc.)
    if (useResizeObserver && "ResizeObserver" in window) {
        ro = new ResizeObserver(debouncedRefresh);
        els.forEach((el) => ro.observe(el));
    }

    // Initial pass: do it after layout settles
    queueMicrotask(refresh);
    // and again on next frame for good measure (fonts/images/layout)
    requestAnimationFrame(refresh);

    return {
        refresh,
        destroy() {
            destroyed = true;
            window.removeEventListener("resize", debouncedRefresh);
            if (ro) ro.disconnect();
            els.forEach((el) => (el.style.height = ""));
        },
    };
}

function normalizeTargets(targets) {
    if (typeof targets === "string") {
        return Array.from(document.querySelectorAll(targets));
    }
    if (targets instanceof Element) return [targets];
    return Array.from(targets || []);
}

function debounce(fn, wait) {
    let t = null;
    return (...args) => {
        if (t) clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
    };
}
